import {
  TBundleLineInActivityLogAction,
  TBundleLineInActivityLogPage,
  TBundleLineInActivityLogRow,
  TBundleLineInTransactionLogPage,
} from '@monorepo/types';
import { TTableOptions, TTableTreeModel } from '@monorepo/ui';
import moment from 'moment';
import { ModifiersZTableModel } from '../../../table.modifier';
import { TTableCellTree } from '../../../table.type';

// Keeps the labels the store data activity log showed.
const EVENT_TYPE_LABEL: Record<TBundleLineInActivityLogAction, string> = {
  checkin: 'added',
  updated: 'updated',
  deleted: 'deleted',
};

// Column keys come from the view descriptor config, which still uses the store data log keys.
const getValue = (
  header: TBundleLineInActivityLogPage['table_config']['table_main_headers'][number],
  item: TBundleLineInActivityLogRow,
): string | number => {
  switch (header.key) {
    case 'datetime':
      return moment(item.event_at).format('DD MMMM') + ', ' + moment(item.event_at).format('hh:mm A');
    case 'actual_qty':
      return item.quantity_scanned;
    case 'event_type':
      return EVENT_TYPE_LABEL[item.action] ?? item.action;
    case 'tagDisplayId':
      return item.tag_display_id;
    case 'bundle_no':
      return item.bundle_no;
    case 'cut_no':
      return item.cut_no ?? '';
    case 'user':
      return item.user;
    default:
      return item.bctx[header.key] ?? '';
  }
};

const getHeaderConfig = (tableConfig: TBundleLineInTransactionLogPage['table_config'], isTabletScreen = false) => {
  const mainHeaders = tableConfig.table_main_headers.map((head, index) => {
    return {
      type: 'TEXT_CELL' as const,
      text: head.display_name,
      align: 'left' as const,
      ...(!isTabletScreen && {
        stickyType: 'LEFT',
        stickyPosition: index + 1,
      }),

      widthInPx: 120,
      maxWidthInPx: 120,
      ...(index === tableConfig.table_main_headers.length - 1 && { borderRight: true }),
    };
  });

  const secondaryHeaders = tableConfig.collapsible_list_config.table_in_row_column.map((head) => {
    return {
      type: 'TEXT_CELL' as const,
      align: 'left' as const,

      text: head.display_name,
    };
  });

  return [...mainHeaders, ...secondaryHeaders];
};

const getRows = (item: TBundleLineInActivityLogRow, tableConfig: TBundleLineInActivityLogPage['table_config']) => {
  const mainRows = tableConfig.table_main_headers.map((head) => {
    const value = getValue(head, item);
    return {
      type: 'TEXT_CELL',
      text: value,
      align: 'left',
    };
  });

  const secondaryRows = tableConfig.collapsible_list_config.table_in_row_column.map((head) => {
    const value = getValue(head, item);
    return {
      type: 'TEXT_CELL',
      text: value,
      align: 'left',
    };
  });

  return [...mainRows, ...secondaryRows];
};

export const BUNDLE_LINE_IN_ACTIVITY_LOG_TABLE = {
  createModel: (
    items: TBundleLineInActivityLogRow[],
    tableConfig: TBundleLineInActivityLogPage['table_config'],
    isTabletScreen: boolean = false,
  ) => {
    return ModifiersZTableModel.createTableModel(
      items,
      {
        headerConfig: getHeaderConfig(tableConfig, isTabletScreen) as TTableOptions['headerConfig'],
      },
      (item) => {
        return {
          type: 'ROW' as const,
          rowItem: item,
          cells: getRows(item, tableConfig) as TTableCellTree[],
        };
      },
    );
  },

  emptyModel: (items: TBundleLineInActivityLogRow[], tableConfig: TBundleLineInActivityLogPage['table_config']) => {
    return ModifiersZTableModel.createTableModel(
      items,
      {
        headerConfig: tableConfig.table_main_headers.map((head, index) => {
          return {
            type: 'TEXT_CELL' as const,
            text: head.display_name,
          };
        }),
      },
      (item) => {
        return {
          type: 'ROW' as const,
          rowItem: item,
          cells: tableConfig.table_main_headers.map((head) => {
            const value = getValue(head, item);
            return {
              type: 'TEXT_CELL' as const,
              text: value,
            };
          }),
        };
      },
    );
  },
};

export type TBundleLineInActivityLogTableType = TTableTreeModel<TBundleLineInActivityLogRow>;
